import HttpClient from "../../utils/http";
import {
  hospitalsBaseURL,
  hospitalSchemesBaseURL,
  hospitalFacilitiesBaseURL,
  hospitalSpecialitiesBaseURL,
} from "../constants/api_endpoints";
import {
  HospitalPayload,
  HospitalMasterItemPayload,
} from "../../views/hospitals/model/hospital";

export interface HospitalListFilters {
  page: number;
  size: number;
  hospital_scheme_id?: number;
  hospital_speciality_id?: number;
  hospital_type?: string;
  state_id?: string;
  district_id?: string;
  search?: string;
  from_date?: string;
  to_date?: string;
  orgId?: number | null;
  sortBy?: string;
  sortOrder?: string;
}

function buildQuery(filters: object): string {
  const params = new URLSearchParams();
  Object.entries(filters as Record<string, unknown>).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  });
  return params.toString();
}

export const getHospitals = (filters: HospitalListFilters) =>
  HttpClient.get(`${hospitalsBaseURL}?${buildQuery(filters)}`);

export const getHospital = (id: number | string, locale: string = "en") =>
  HttpClient.get(`${hospitalsBaseURL}/${id}`, { headers: { locale } });

export const createHospital = (payload: HospitalPayload) =>
  HttpClient.post(hospitalsBaseURL, payload);

export const updateHospital = (
  id: number | string,
  payload: Partial<HospitalPayload>,
  locale: string = "en"
) => HttpClient.patch(`${hospitalsBaseURL}/${id}`, payload, { headers: { locale } });

export const deleteHospital = (id: number | string) =>
  HttpClient.delete(`${hospitalsBaseURL}/${id}`);

// Hospital Schemes (master list used by the hospital form's multi-select,
// and manageable in their own right via the Hospital Schemes CRUD screen).
export const getHospitalSchemes = (filters: { page: number; size: number; orgId?: number | null }) =>
  HttpClient.get(`${hospitalSchemesBaseURL}?${buildQuery(filters)}`);

export const createHospitalScheme = (payload: HospitalMasterItemPayload) =>
  HttpClient.post(hospitalSchemesBaseURL, payload);

export const updateHospitalScheme = (
  id: number | string,
  payload: HospitalMasterItemPayload
) => HttpClient.patch(`${hospitalSchemesBaseURL}/${id}`, payload);

export const deleteHospitalScheme = (id: number | string) =>
  HttpClient.delete(`${hospitalSchemesBaseURL}/${id}`);

// Hospital Facilities
export const getHospitalFacilities = (filters: { page: number; size: number; orgId?: number | null }) =>
  HttpClient.get(`${hospitalFacilitiesBaseURL}?${buildQuery(filters)}`);

export const createHospitalFacility = (payload: HospitalMasterItemPayload) =>
  HttpClient.post(hospitalFacilitiesBaseURL, payload);

export const updateHospitalFacility = (
  id: number | string,
  payload: HospitalMasterItemPayload
) => HttpClient.patch(`${hospitalFacilitiesBaseURL}/${id}`, payload);

export const deleteHospitalFacility = (id: number | string) =>
  HttpClient.delete(`${hospitalFacilitiesBaseURL}/${id}`);

// Hospital Specialities
export const getHospitalSpecialities = (filters: { page: number; size: number; orgId?: number | null }) =>
  HttpClient.get(`${hospitalSpecialitiesBaseURL}?${buildQuery(filters)}`);

export const createHospitalSpeciality = (payload: HospitalMasterItemPayload) =>
  HttpClient.post(hospitalSpecialitiesBaseURL, payload);

export const updateHospitalSpeciality = (
  id: number | string,
  payload: HospitalMasterItemPayload
) => HttpClient.patch(`${hospitalSpecialitiesBaseURL}/${id}`, payload);

export const deleteHospitalSpeciality = (id: number | string) =>
  HttpClient.delete(`${hospitalSpecialitiesBaseURL}/${id}`);

export function downloadHospitalsBulkUploadTemplate() {
  return HttpClient.get(`${hospitalsBaseURL}/bulk-upload/template`, { responseType: "blob" });
}

export function bulkUploadHospitals(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return HttpClient.post(`${hospitalsBaseURL}/bulk-upload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export function downloadHospitalTranslationsBulkUploadTemplate() {
  return HttpClient.get(`${hospitalsBaseURL}/bulk-upload/translations/template`, { responseType: "blob" });
}

export function bulkUploadHospitalTranslations(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return HttpClient.post(`${hospitalsBaseURL}/bulk-upload/translations`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}
